import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faUmbrellaBeach } from "@fortawesome/free-solid-svg-icons";
import { Card, CardBody, CardHeader, Collapse, Input, Label } from "reactstrap";
import { useHibobApi } from "../../../Integrations/HiBob/api";
import { getWritebackIssueKey } from "../../../Integrations/HiBob/messages";
import "./TimeoffCard.scss";

const count = (value) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

/** Saved mapping first, then the suggestion, else not sent. */
const initialSelection = (view) => {
	const selection = {};
	for (const type of view.traxxionTypes || []) {
		selection[type.id] = (view.mapping || {})[type.id] || (view.suggestions || {})[type.id] || "";
	}
	return selection;
};

function WritebackCounts({ writeback, t }) {
	const [open, setOpen] = useState(false);
	if (!writeback) {
		return null;
	}
	const issues = (Array.isArray(writeback.issues) ? writeback.issues : []).filter((issue) => count(issue.count) > 0);
	const issueTotal = issues.reduce((sum, issue) => sum + count(issue.count), 0);

	return (
		<div className="hibob-wb-counts" data-testid="hibob-writeback-counts">
			<span>{t("hibob-wb-sent-today", { count: count(writeback.sentToday) })}</span>
			<span aria-hidden="true"> · </span>
			<span>{t("hibob-wb-confirmed", { count: count(writeback.confirmed) })}</span>
			<span aria-hidden="true"> · </span>
			{issueTotal ? (
				<button
					type="button"
					className="hibob-issue-chip"
					aria-expanded={open}
					aria-controls="hibob-wb-issues"
					onClick={() => setOpen((value) => !value)}
				>
					{t("hibob-wb-issues", { count: issueTotal })}
					<FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} size="xs" aria-hidden="true" />
				</button>
			) : (
				<span className="hibob-zero">{t("hibob-wb-no-issues")}</span>
			)}
			{issueTotal > 0 && (
				<Collapse isOpen={open} id="hibob-wb-issues">
					<ul className="hibob-issue-list" aria-label={t("hibob-col-issues")}>
						{issues.map((issue) => (
							<li key={issue.code}>
								{t("hibob-issue-line", { count: count(issue.count), text: t(getWritebackIssueKey(issue.code)) })}
							</li>
						))}
					</ul>
				</Collapse>
			)}
		</div>
	);
}

/**
 * Time off to HiBob: which Traxxion time off type goes to which HiBob policy
 * type, and how the pushes went. Approval sends nothing until a type is
 * mapped and saved; suggestions are only pre-filled.
 */
function TimeoffCard({ writeback }) {
	const { t } = useTranslation();
	const { getTimeoffMapping, saveTimeoffMapping } = useHibobApi();

	// loading | ready | unreadable | error
	const [state, setState] = useState("loading");
	const [view, setView] = useState(null);
	const [selection, setSelection] = useState({});
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const load = useCallback(async () => {
		setState("loading");
		try {
			const response = await getTimeoffMapping();
			if (!mounted.current || !response) {
				return;
			}
			if (response.status === 200 && Array.isArray(response.data?.traxxionTypes)) {
				setView(response.data);
				setSelection(initialSelection(response.data));
				setState("ready");
			} else if (response.status === 409 && response.data?.reason === "policy_types_unreadable") {
				setState("unreadable");
			} else {
				setState("error");
			}
		} catch (err) {
			if (mounted.current) {
				setState("error");
			}
		}
	}, [getTimeoffMapping]);

	useEffect(() => {
		load();
	}, [load]);

	const onSave = useCallback(async () => {
		setSaving(true);
		setSaveError(null);
		try {
			const response = await saveTimeoffMapping(selection);
			if (!mounted.current || !response) {
				return;
			}
			if (response.status === 200 && Array.isArray(response.data?.traxxionTypes)) {
				setView(response.data);
				setSelection(initialSelection(response.data));
				toast.success(t("hibob-wb-saved"));
			} else if (response.status === 400 && response.data?.reason === "unknown_policy_type") {
				setSaveError(t("hibob-wb-unknown-policy"));
				await load();
			} else {
				setSaveError(t("hibob-error-generic"));
			}
		} catch (err) {
			if (mounted.current) {
				setSaveError(t("hibob-error-generic"));
			}
		} finally {
			if (mounted.current) {
				setSaving(false);
			}
		}
	}, [saveTimeoffMapping, selection, t, load]);

	let body;
	if (state === "loading") {
		body = <p className="hibob-muted mb-0">{t("hibob-wb-loading")}</p>;
	} else if (state === "unreadable") {
		body = <p className="hibob-muted mb-0">{t("hibob-wb-unreadable")}</p>;
	} else if (state === "error") {
		body = (
			<div role="alert" className="d-flex align-items-center flex-wrap">
				<span className="mr-3">{t("hibob-wb-load-error")}</span>
				<button type="button" className="btn btn-sm btn-secondary" onClick={load}>
					{t("hibob-retry")}
				</button>
			</div>
		);
	} else if (!view.traxxionTypes.length) {
		body = <p className="hibob-muted mb-0">{t("hibob-wb-no-types")}</p>;
	} else {
		const policies = view.hibobPolicyTypes || [];
		body = (
			<>
				<ul className="list-unstyled mb-3">
					{view.traxxionTypes.map((type) => {
						const id = `hibob-wb-${type.id}`;
						const suggested = !(view.mapping || {})[type.id] && !!(view.suggestions || {})[type.id];
						return (
							<li key={type.id} className="hibob-wb-row">
								<Label for={id} className="hibob-wb-type mb-0">
									{type.name}
								</Label>
								<Input
									id={id}
									type="select"
									bsSize="sm"
									value={selection[type.id] || ""}
									onChange={(e) => setSelection((current) => ({ ...current, [type.id]: e.target.value }))}
									disabled={saving}
								>
									<option value="">{t("hibob-wb-not-sent")}</option>
									{policies.map((name) => (
										<option key={name} value={name}>
											{name}
										</option>
									))}
								</Input>
								{suggested && selection[type.id] === view.suggestions[type.id] && (
									<span className="hibob-wb-suggested">{t("hibob-wb-suggested")}</span>
								)}
							</li>
						);
					})}
				</ul>
				{saveError && (
					<p role="alert" className="text-danger text-sm">
						{saveError}
					</p>
				)}
				<button type="button" className="btn btn-sm btn-primary" onClick={onSave} disabled={saving}>
					{saving ? t("hibob-wb-saving") : t("hibob-wb-save")}
				</button>
			</>
		);
	}

	return (
		<Card data-testid="hibob-timeoff-card">
			<CardHeader>
				<h2 className="hibob-card-title">
					<FontAwesomeIcon icon={faUmbrellaBeach} className="mr-2" aria-hidden="true" />
					{t("hibob-wb-title")}
				</h2>
			</CardHeader>
			<CardBody>
				<p className="hibob-muted text-sm">{t("hibob-wb-explainer")}</p>
				<WritebackCounts writeback={writeback} t={t} />
				{body}
			</CardBody>
		</Card>
	);
}

export default TimeoffCard;
